import { useQuery } from '@tanstack/react-query';

import { isPrometheusConfigured } from '@/shared/config/prometheus-config';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { monitoringKeys } from '@/shared/lib/query/queryKeys';

import {
  BYTES_TO_KB,
  getInstantValue,
  getRangeValues,
  getRangeValuesByPod,
  PROMETHEUS_QUERIES,
  QUERY_INTERVALS,
  RANGE_SECONDS,
  REFETCH_INTERVAL,
  STALE_TIME,
} from '@/features/monitoring/api';
import type { DiskUsageData } from '@/features/monitoring/api';

import { assignOrderedPodSeriesColors } from './lib/assignOrderedPodSeriesColors';

export const useDiskUsage = () => {
  const enabled = isPrometheusConfigured();

  const summaryQuery = useQueryWithBoundary<{ readCurrent: number; writeCurrent: number; unit: DiskUsageData['unit'] }>(
    {
      queryKey: monitoringKeys.disk(),
      queryFn: async () => {
        const [readValue, writeValue] = await Promise.all([
          getInstantValue(PROMETHEUS_QUERIES.DISK.READ),
          getInstantValue(PROMETHEUS_QUERIES.DISK.WRITE),
        ]);

        return {
          readCurrent: readValue / BYTES_TO_KB,
          writeCurrent: writeValue / BYTES_TO_KB,
          unit: 'KB/s',
        };
      },
      enabled,
      refetchInterval: REFETCH_INTERVAL,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      ...DEFAULT_QUERY_OPTIONS,
    }
  );

  const detailQuery = useQuery<{
    read: Pick<DiskUsageData['read'], 'history' | 'podHistory'>;
    write: Pick<DiskUsageData['write'], 'history' | 'podHistory'>;
  }>({
    queryKey: [...monitoringKeys.diskPrefix(), 'detail'],
    queryFn: async () => {
      const now = Math.floor(Date.now() / 1000);

      const [readHistory, writeHistory, readPodHistory, writePodHistory] = await Promise.all([
        getRangeValues(PROMETHEUS_QUERIES.DISK.READ_HISTORY, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValues(PROMETHEUS_QUERIES.DISK.WRITE_HISTORY, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValuesByPod(PROMETHEUS_QUERIES.DISK.READ_BY_POD, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValuesByPod(PROMETHEUS_QUERIES.DISK.WRITE_BY_POD, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
      ]);

      const readPodHistoryWithColors = assignOrderedPodSeriesColors(
        readPodHistory.map((podData) => ({
          ...podData,
          data: podData.data.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
        }))
      );

      const writePodHistoryWithColors = assignOrderedPodSeriesColors(
        writePodHistory.map((podData) => ({
          ...podData,
          data: podData.data.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
        }))
      );

      return {
        read: {
          history: readHistory.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
          podHistory: readPodHistoryWithColors,
        },
        write: {
          history: writeHistory.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
          podHistory: writePodHistoryWithColors,
        },
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data: DiskUsageData | undefined = summaryQuery.data
    ? {
        read: {
          current: summaryQuery.data.readCurrent,
          history: detailQuery.data?.read.history ?? [],
          podHistory: detailQuery.data?.read.podHistory ?? [],
        },
        write: {
          current: summaryQuery.data.writeCurrent,
          history: detailQuery.data?.write.history ?? [],
          podHistory: detailQuery.data?.write.podHistory ?? [],
        },
        unit: summaryQuery.data.unit,
      }
    : undefined;

  return {
    ...summaryQuery,
    data,
    isFetching: summaryQuery.isFetching || detailQuery.isFetching,
    isLoading: summaryQuery.isLoading,
  };
};
